import { Link } from "react-router-dom";
import FooterLinks from "@/components/FooterLinks";
import { Button } from "@/components/ui/button";
import MarketingTopBar from "@/components/MarketingTopBar";
import PriceCompareCarousel from "@/components/PriceCompareCarousel";
import FeatureHub from "@/components/FeatureHub";
import { useAuthModal } from "@/context/AuthModalContext";
import {
  Store, MessagesSquare, Users, Search, Trophy,
  ArrowRight, ShieldCheck, ChevronRight, Award,
} from "lucide-react";

const IMG = {
  hero: "https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/4e0lcgl1_IMG_E4255%20PLSR%20SRC%20HOME%202.jfif",
  market: "https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/p8gkcgu0_DSC_7605.webp",
  poweredBy:
    "https://customer-assets-rejwkqb3.emergentagent.net/job_throttle-hub-6/artifacts/krvfdgqm_Powered%20by%20PLSR%20%28cropped%29.png",
};

const FEATURES = [
  {
    icon: Store,
    title: "Marketplace",
    desc: "Buy and sell wheels, pedals, rigs and more with secure payment and affordable shipping, plus hire specialists for setups, liveries, rig builds and coaching.",
    img: "/banners/src-marketplace-banner-2.webp",
    link: "/learn/marketplace",
  },
  {
    icon: Award,
    title: "Sponsorship Portal",
    desc: "Support that can grow from first manufacturer deals to being paid to race.",
    img: "/banners/src-sponsor-banner.webp",
    link: "/learn/sponsorship",
  },
  {
    icon: Search,
    title: "Price Compare",
    desc: "Compare prices, stock, and shipping across multiple stores all in one place",
    img: "/banners/src-market-banner.jpg",
    link: "/learn/price-compare",
  },
  {
    icon: MessagesSquare,
    title: "Forum",
    desc: "Talk setups, leagues, gear, reviews, race craft, and more.",
    img: "https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/xvifalk7_PLSRC-SRC-Forum.webp",
    link: "/learn/how-it-works",
  },
  {
    icon: Users,
    title: "Paddock Banter",
    desc: "Post race results and achievements directly from sim hub. Connect, share news and show setups.",
    img: "https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/fswwj2qo_PLSRC-SRC-Paddock%20talk%202.webp",
    link: "/learn/how-it-works",
  },
  {
    icon: Trophy,
    title: "Grid Points",
    desc: "Use the platform. Earn rewards.",
    img: "https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/wdd80hsj_PLSRC-SRC-Gridpoints.webp",
    link: "/learn/grid-points",
  },
];

const STEPS = [
  { n: "01", title: "Create your account", desc: "Join SRC: create a simple account to help us moderate and keep the community genuine and spam-free." },
  { n: "02", title: "Set up your profile", desc: "Link SimHub and edit your sim racer profile to build your brand." },
  { n: "03", title: "Race, trade & connect", desc: "Buy and sell safely, launch your sim racing career, shop, discuss, and share laps and achievements to grow your brand." },
];

function TopBar() {
  return <MarketingTopBar transparentAtTop />;
}

function SectionHeading({ kicker, title, sub, kickerColor, titleColor, subColor, kickerSize, subSize, titleClassName, maxW = "max-w-2xl", noAccent, accentBelow }) {
  return (
    <div className={`mb-10 ${maxW}`}>
      <h2
        className={`font-display font-bold uppercase leading-[1.05] ${noAccent ? "" : "relative pl-5"}`}
        style={{ fontSize: kickerSize || "2.27rem", ...(kickerColor ? { color: kickerColor } : {}) }}
      >
        {!noAccent && <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-primary" />}
        {kicker}
      </h2>
      {accentBelow && <span aria-hidden className="mt-5 block h-2 w-20 rounded-full bg-primary" />}
      {title && (
        <p
          className={titleClassName || "mt-3 font-display text-[1.41rem] font-bold sm:text-[1.69rem]"}
          style={titleColor ? { color: titleColor } : undefined}
        >
          {title}
        </p>
      )}
      {sub && (
        <p
          className="mt-3 max-w-2xl text-muted-foreground"
          style={{ ...(subSize ? { fontSize: subSize } : {}), ...(subColor ? { color: subColor } : {}) }}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

export default function Landing() {
  const { openAuth } = useAuthModal();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopBar />

      {/* Hero */}
      <section className="src-hero-section relative flex min-h-[92vh] items-center overflow-hidden bg-background pt-32 sm:pt-44 lg:pt-56">
        {/* Right-anchored 3:2 hero image */}
        <div className="src-hero-image-wrap pointer-events-none absolute inset-y-0 right-0 flex items-center">
          <div className="src-hero-image relative h-full aspect-[3/2] max-w-[85vw]">
            <div
              className="src-hero-image__photo absolute inset-0 bg-cover opacity-75"
              style={{ backgroundImage: `url(${IMG.hero})`, backgroundPosition: "95% 8%" }}
            />
            {/* Left fade into the black section background */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(to right, hsl(240 8% 4%) 0%, hsl(240 8% 4% / 0.55) 55%, hsl(240 8% 4% / 0.10) 100%)",
              }}
            />
            {/* Soft top/bottom vignettes so the block blends with the section edges */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(to bottom, hsl(240 8% 4% / 0.55) 0%, transparent 45%, hsl(240 8% 4% / 0.70) 100%)",
              }}
            />
          </div>
        </div>

        {/* Ownable brand decoration: oversized race number */}
        <span className="ghost-number absolute -right-6 top-10 hidden select-none text-[26rem] lg:block">01</span>
        <div className="checker absolute right-0 top-0 hidden h-full w-3 opacity-30 lg:block" />

        <div className="src-container relative mx-auto w-full max-w-7xl px-4 sm:px-6">
          <div className="max-w-6xl animate-fade-up">
            <h1 className="font-display font-bold uppercase leading-[1.02]">
              <span
                className="block whitespace-nowrap"
                style={{ fontSize: "clamp(1.2rem, 5.1vw, 4rem)" }}
              >
                Buy. Sell. <span className="text-gradient-primary-soft">Get Sponsored.</span>
              </span>
            </h1>
            <p className="mt-4 max-w-2xl text-xl leading-relaxed sm:text-2xl" style={{ color: "#d8dadf" }}>
              Buy and sell gear, share setups and results, find sponsorships, and shop across sim shops and retailers in one place before you spend.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => openAuth("register")} className="h-14 px-10 text-lg bg-primary text-primary-foreground hover:bg-primary/90" data-testid="hero-primary-cta">
                Join the grid <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => openAuth("login")} className="h-14 px-10 text-lg border-border" data-testid="hero-secondary-cta">
                Sign in
              </Button>
            </div>
          </div>
        </div>
        <div className="racing-line absolute bottom-0 left-0 right-0" />
      </section>

      {/* Features */}
      <section id="features" className="relative overflow-hidden" style={{ backgroundColor: "#eceef1" }}>
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url(https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/6ec8c7dc47c4e2a2_DJI_20240531173253_0024_D.webp)" }}
          />
          <div
            className="absolute inset-0"
            style={{ backgroundImage: "linear-gradient(to bottom, #eceef1 0%, #eceef1 16%, rgba(236,238,241,0.8) 40%, rgba(236,238,241,0.8) 100%)" }}
          />
        </div>
        <div className="relative src-container mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <SectionHeading
          kicker={<><span className="text-primary">One platform</span>, everything you need</>}
          kickerColor="#2b2b30"
          kickerSize="2.86rem"
          subColor="#3f3f46"
          subSize="1.2rem"
          maxW="max-w-none"
          noAccent
          accentBelow
          sub="Buying and selling, shopping new gear, forums, achievements, sponsorships, and shopping. Everything lives together, under one roof, with the details done right."
        />
        {/* Interactive hub — desktop/tablet */}
        <div className="mt-14 hidden md:block">
          <FeatureHub items={FEATURES} />
        </div>
        {/* Stacked fallback — small screens (no hover/skew cluster) */}
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:hidden">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <Link
                key={f.title}
                to={f.link}
                className="group relative block overflow-hidden rounded-lg border border-border bg-card"
                data-testid={`feature-hub-mobile-${f.title.toLowerCase().replace(/\s|&/g, "-")}`}
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={f.img} alt={f.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-black/30" />
                  <div className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-md bg-primary">
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-display text-lg font-bold uppercase">{f.title}</h3>
                  <p className="mt-1.5 text-sm" style={{ color: "#c7c8cf" }}>{f.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
        </div>
      </section>

      {/* Marketplace showcase */}
      <section id="marketplace" className="relative overflow-hidden border-y border-border bg-carbon">
        <span className="ghost-number absolute -left-4 bottom-0 hidden text-[20rem] lg:block">02</span>
        <div className="src-container relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 sm:px-6 lg:grid-cols-2">
          <div>
            <SectionHeading
              kicker="Marketplace"
              subColor="#c7c8cf"
              subSize="1.15rem"
              sub="From a set of pedals to a full motion rig: buy or list in minutes. Payment is held until the buyer confirms delivery, with protection handled by people who actually know the hardware. Beyond hardware, the Marketplace connects you with sim racing specialists for custom car setups, liveries, full rig builds and one-on-one coaching."
            />
            <ul className="space-y-3">
              {[
                "Buyer and seller protection on every sale",
                "Hire specialists for setups, liveries, rig builds and coaching",
                "Disagreements reviewed by people who know sim racing gear",
                "Local pickup or shipping, your call",
                "Packing supplies and shipping labels in one click",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-base">
                  <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                  <span style={{ color: "#c7c8cf" }}>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/learn/marketplace" className="mt-8 inline-block">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="marketplace-cta">
                Explore the marketplace <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="card-hover relative overflow-hidden rounded-lg border border-border">
            <div className="aspect-[4/3] bg-cover bg-center" style={{ backgroundImage: `url(${IMG.market})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
            <div className="absolute bottom-4 left-4 speed-hatch rounded px-3 py-1 text-xs font-semibold uppercase tracking-wider text-foreground">
              Payment protected
            </div>
          </div>
        </div>
      </section>

      {/* Sponsorship */}
      <section id="sponsorship" style={{ backgroundColor: "#eceef1" }}>
        <div className="src-container mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 sm:px-6 lg:grid-cols-2">
        <div className="card-hover relative order-2 overflow-hidden rounded-lg border border-border lg:order-1">
          <div
            className="aspect-[4/3] bg-cover bg-center"
            style={{
              backgroundImage:
                "url(https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/zv9j1qnb__DSC0378%20%281%29.webp)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
          <div className="absolute bottom-4 left-4 speed-hatch rounded px-3 py-1 text-xs font-semibold uppercase tracking-wider text-foreground">
            Get sponsored
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <SectionHeading
            kicker="Sponsorship"
            kickerColor="#0b0b0d"
            subColor="#3f3f46"
            subSize="1.15rem"
            sub="Launch your sim racing career and access the tools to grow it. Apply through the SRC Sponsorship portal, track it live, and stay in touch with the team directly. From early backing to getting paid to race all the way to finding your way into a real car:"
          />
          <ul className="space-y-3">
            {[
              "One application flow, multiple manufacturers",
              "Direct access to the sponsorship team",
              "Progress and status, tracked in one dashboard",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-base">
                <Award className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <span style={{ color: "#3f3f46" }}>{t}</span>
              </li>
            ))}
          </ul>
          <Link to="/learn/sponsorship" className="mt-8 inline-block">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="sponsorship-cta">
              Apply for sponsorship <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
        </div>
      </section>

      {/* Price Compare */}
      <PriceCompareCarousel />

      {/* How it works — teaser link to standalone page */}
      <section id="how" style={{ backgroundColor: "#eceef1" }}>
        <div className="src-container mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <SectionHeading
          kicker="How it works"
          kickerColor="#0b0b0d"
          subColor="#3f3f46"
          subSize="1.15rem"
          title="Everything sim racers need, connected"
          titleClassName="mt-1 font-display text-[1.14rem] font-medium uppercase tracking-wide text-primary sm:text-[1.37rem]"
          sub="Buy, sell, compare new prices on gear, and find sponsors all in one place. Bring in your telemetry, experience, and achievements to launch your sim racing career and build your name."
        />
        <div className="mb-10 grid gap-6 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="relative rounded-lg border border-border bg-card p-6">
              <span className="font-display text-5xl font-bold text-primary/70">{s.n}</span>
              <h3 className="mt-2 font-display text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-base" style={{ color: "#c7c8cf" }}>{s.desc}</p>
            </div>
          ))}
        </div>
        <Link to="/learn/how-it-works">
          <Button size="lg" variant="outline" className="border-neutral-400 bg-white text-neutral-800 hover:bg-neutral-100" data-testid="how-it-works-link">
            See a full walk-through &amp; FAQ <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden border-t border-border">
        <div className="speed-hatch absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-3xl px-4 py-24 text-center sm:px-6">
          <h2 className="font-display text-4xl font-bold uppercase sm:text-5xl">
            Take your place on the <span className="text-gradient-primary-soft">grid</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Join a community built by sim racers, for sim racers. Create your free account and transform how you sim race and shop.
          </p>
          <Button size="lg" onClick={() => openAuth("register")} className="mt-8 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="final-cta">
            Create your account <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <div className="racing-line absolute bottom-0 left-0 right-0" />
      </section>

      {/* Footer */}
      <footer className="src-container mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-10 sm:px-6">
        <img
          src={IMG.poweredBy}
          alt="Powered by PLSR"
          className="h-10 w-auto opacity-90"
          draggable={false}
          data-testid="landing-powered-by"
        />
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <FooterLinks tone="light" />
        </div>
      </footer>
    </div>
  );
}
